import { useCallback, useEffect, useRef, useState } from 'react';
import type { QueryKey } from '@tanstack/react-query';
import { useToggleLiveContextMutation } from '@/state/mutations/player/use-toggle-live-context';
import { useFeatureAccess } from '@/hooks/use-feature-access';
import { useGoLivePreviewHold } from '@/state/stores/use-go-live-preview-hold.store';
import { useInsufficientPlayoutHoursInfo } from '@/hooks/use-insufficient-playout-hours-info';
import { requiresEventSlotForLiveBinding } from '@/helpers/live-binding-context.helper';
import { useEventStore } from '@/state/stores/use-event-store';
import { useFlowStore } from '@/state/stores/use-flow-store';
import type { FlowDto } from '@/types/dtos/flows/response/flow.dto';
import { getObjectId, resolveLiveBindingFlowId } from '@/helpers/object.id.helper';

function storeFlowMatchesProp(storeFlow: FlowDto | undefined, propFlow: FlowDto): boolean {
  if (!storeFlow) return false;
  if (storeFlow.id === propFlow.id) return true;
  const storeOrigin = storeFlow.origin ? getObjectId(storeFlow.origin) : storeFlow.id;
  const propOrigin = propFlow.origin ? getObjectId(propFlow.origin) : propFlow.id;
  if (storeOrigin === propOrigin) return true;
  if (storeFlow.liveBindingFlowId && storeFlow.liveBindingFlowId === propFlow.liveBindingFlowId) return true;
  return false;
}

/** Unblock the return-to-simulation modal if the toggle API never settles. */
const LIVE_CONTEXT_TOGGLE_UI_TIMEOUT_MS = 45_000;

export function useLiveContextFlowToggle({
  flow,
  eventSlotId,
  flowQueryKey,
}: {
  flow: FlowDto;
  eventSlotId?: string | null;
  flowQueryKey?: QueryKey;
}) {
  const { hasAccess: hasPerformAccess, isLoading: performAccessLoading } = useFeatureAccess('perform');
  const event = useEventStore((s) => s.event);
  const slotContextRequired = requiresEventSlotForLiveBinding({
    multiFlow: event?.multiFlow,
    eventSlotId,
  });
  const { info: insufficientPlayout, isChecking: playoutCheckPending } = useInsufficientPlayoutHoursInfo(flow);
  const toggleLiveContextMutation = useToggleLiveContextMutation();
  // Prefer zustand (WS / mutation patches) over the React Query prop, which can stay stale
  // until GET_FLOW refetch completes — otherwise the SIMULATION/LIVE switch does not flip.
  const storeIsRunning = useFlowStore((s) => {
    const candidates = [s.flow, s.flowPlan, s.flowOrganize];
    for (const candidate of candidates) {
      if (storeFlowMatchesProp(candidate, flow)) {
        return candidate?.liveContext?.isRunning ?? false;
      }
    }
    return undefined;
  });
  const hasRunningLiveContext = storeIsRunning ?? flow?.liveContext?.isRunning ?? false;
  const [isToggling, setIsToggling] = useState(false);
  const toggleSettledRef = useRef<(() => void) | undefined>(undefined);
  const holdUiRef = useRef(false);
  const holdGenerationRef = useRef<number | null>(null);
  const flowEntityId = resolveLiveBindingFlowId(flow, eventSlotId ?? undefined);

  const endToggleUi = useCallback(() => {
    holdUiRef.current = false;
    setIsToggling(false);
  }, []);

  const toggleLiveContext = useCallback(
    (options?: { onSettled?: () => void; holdUi?: boolean }) => {
      if (!flow?.id || isToggling) return;

      holdUiRef.current = options?.holdUi === true;
      holdGenerationRef.current = options?.holdUi === true ? useGoLivePreviewHold.getState().hold(flowEntityId) : null;
      toggleLiveContextMutation.mutate(
        {
          entityType: 'Flow',
          entityId: flowEntityId,
          ...(eventSlotId ? { eventSlotId } : {}),
          flowQueryKey,
        },
        {
          onSettled: (_data, error) => {
            if (error) {
              const generation = holdGenerationRef.current;
              if (generation != null) {
                useGoLivePreviewHold.getState().release(flowEntityId, generation);
              }
            }
            if (!holdUiRef.current || error) {
              setIsToggling(false);
            }
            toggleSettledRef.current?.();
            toggleSettledRef.current = undefined;
          },
        },
      );
      toggleSettledRef.current = options?.onSettled;
      setIsToggling(true);
    },
    [eventSlotId, flow, flowEntityId, flowQueryKey, isToggling, toggleLiveContextMutation],
  );

  useEffect(() => {
    if (!isToggling) return;
    const timer = window.setTimeout(() => {
      const generation = holdGenerationRef.current;
      if (generation != null) {
        useGoLivePreviewHold.getState().release(flowEntityId, generation);
      }
      endToggleUi();
      toggleSettledRef.current?.();
      toggleSettledRef.current = undefined;
    }, LIVE_CONTEXT_TOGGLE_UI_TIMEOUT_MS);
    return () => window.clearTimeout(timer);
  }, [endToggleUi, flowEntityId, isToggling]);

  useEffect(() => {
    const owner = flowEntityId;
    return () => {
      const generation = holdGenerationRef.current;
      if (generation == null) {
        return;
      }
      useGoLivePreviewHold.getState().release(owner, generation);
    };
  }, [flowEntityId]);

  const hasPerform = !performAccessLoading && hasPerformAccess;
  const playoutBlocksLive = Boolean(insufficientPlayout);
  // Hold go-live until the balances query settles, otherwise a pending check reads as sufficient.
  const canStartLive = hasPerform && !slotContextRequired && !playoutBlocksLive && !playoutCheckPending;
  // Keep the switch visible when perform is allowed but playout hours block go-live.
  const canShowToggle = !slotContextRequired ? hasRunningLiveContext || hasPerform : hasRunningLiveContext;

  return {
    hasRunningLiveContext,
    isToggling,
    endToggleUi,
    toggleLiveContext,
    canShowToggle,
    canStartLive,
    slotContextRequired,
    playoutBlocksLive,
    insufficientPlayout,
    previewHoldOwner: flowEntityId,
  };
}
